import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";

const CATEGORIES = [
  ["tournaments", "Tournament Questions"],
  ["streaming", "Streaming on GAStreaming"],
  ["collaborations", "Collaborations"],
  ["account", "Account Support"],
  ["business", "Business Inquiries"],
  ["other", "Other"],
];

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getStoredUser() {
  const raw =
    localStorage.getItem("ga_user") ||
    localStorage.getItem("user") ||
    localStorage.getItem("member_user") ||
    "";
  const parsed = raw ? safeJsonParse(raw) : null;
  if (parsed && typeof parsed === "object") return parsed;
  return {
    username: localStorage.getItem("username") || localStorage.getItem("member_username") || "",
    email: localStorage.getItem("email") || localStorage.getItem("member_email") || "",
  };
}

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

function isQuestionAdmin(user) {
  if (user?.isAdmin || String(user?.role || "").toLowerCase() === "admin") return true;
  const configured = String(process.env.REACT_APP_ADMIN_EMAILS || "peteywhiskers@yahoo.com")
    .split(",")
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
  return configured.includes(String(user?.email || "").trim().toLowerCase());
}

export default function Contact() {
  const user = useMemo(() => getStoredUser(), []);
  const signedIn = Boolean(getAuthHeader());
  const [form, setForm] = useState({
    name: String(user?.displayName || user?.name || ""),
    email: String(user?.email || ""),
    username: String(user?.username || user?.name || ""),
    category: "tournaments",
    message: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submitQuestion(event) {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSuccess("");
    setError("");

    try {
      const authHeader = getAuthHeader();
      const response = await fetch(apiUrl("/tournaments/questions"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify(form),
        mode: "cors",
        credentials: "omit",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.error || "Could not submit your question.");
      }

      setSuccess(
        data?.question?.linkedToAccount
          ? "Your question was sent. When GAStreaming responds, you’ll receive an email and a notification through the bell."
          : "Your question was sent. You’ll receive the response by email."
      );
      setForm((current) => ({ ...current, message: "" }));
    } catch (submitError) {
      setError(submitError?.message || "Could not submit your question right now.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.eyebrow}>CONTACT GAStreaming</div>
        <h1 style={styles.title}>Got Questions?</h1>
        <p style={styles.subtitle}>
          Have questions about tournaments, collaborations, streaming on GAStreaming.live, or anything else happening on the platform? Send your question here and we’ll respond as soon as possible.
        </p>
        <div style={styles.deliveryNote}>
          {signedIn
            ? "Because you’re signed in, your response can appear in your notification bell and will also be emailed to you."
            : "No account is required. Guest questions receive their response by email."}
        </div>
        {isQuestionAdmin(user) ? (
          <Link to="/admin/questions" style={styles.adminLink}>
            Open Admin Question Inbox →
          </Link>
        ) : null}
      </section>

      <form onSubmit={submitQuestion} style={styles.formCard}>
        <div style={styles.formHeadingRow}>
          <div>
            <div style={styles.eyebrow}>SEND A QUESTION</div>
            <h2 style={styles.formTitle}>What can we help with?</h2>
          </div>
          <span style={styles.accountPill}>{signedIn ? "PROFILE CONNECTED" : "GUEST QUESTION"}</span>
        </div>

        <div style={styles.grid}>
          <label style={styles.label}>
            Name
            <input
              type="text"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              maxLength={120}
              required
              style={styles.input}
              placeholder="Your name"
              autoComplete="name"
            />
          </label>

          <label style={styles.label}>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              maxLength={180}
              required
              style={styles.input}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>

          <label style={styles.label}>
            GAStreaming username <span style={styles.optional}>(optional)</span>
            <input
              type="text"
              value={form.username}
              onChange={(event) => updateField("username", event.target.value)}
              maxLength={80}
              style={styles.input}
              placeholder="Your username"
              autoComplete="username"
            />
          </label>

          <label style={styles.label}>
            Question category
            <select
              value={form.category}
              onChange={(event) => updateField("category", event.target.value)}
              style={styles.input}
            >
              {CATEGORIES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label style={{ ...styles.label, marginTop: 16 }}>
          Your question
          <textarea
            value={form.message}
            onChange={(event) => updateField("message", event.target.value)}
            minLength={10}
            maxLength={5000}
            rows={8}
            required
            style={{ ...styles.input, ...styles.textarea }}
            placeholder="Tell us what you need to know about a tournament, collaboration, streaming on the platform, your account, or another GAStreaming topic."
          />
        </label>

        <label style={styles.honeypot} aria-hidden="true">
          Website
          <input
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(event) => updateField("website", event.target.value)}
          />
        </label>

        {success ? <div style={styles.success}>{success}</div> : null}
        {error ? <div style={styles.error}>{error}</div> : null}

        <div style={styles.actionRow}>
          <div style={styles.responseCopy}>
            Questions tied to a profile can be opened from the notification bell after they are answered.
          </div>
          <button type="submit" disabled={submitting} style={styles.submitButton}>
            {submitting ? "Sending…" : "Send Question"}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1080, margin: "0 auto", padding: "18px 16px 80px" },
  hero: {
    borderRadius: 24,
    padding: "28px clamp(18px, 4vw, 42px)",
    border: "1px solid rgba(221,53,65,.34)",
    background: "linear-gradient(135deg, rgba(91,8,16,.68), rgba(7,7,10,.92))",
    boxShadow: "0 24px 70px rgba(0,0,0,.44)",
  },
  eyebrow: { color: "#ff9da4", fontWeight: 900, letterSpacing: ".16em", fontSize: 12 },
  title: { margin: "8px 0 10px", color: "#fff", fontSize: "clamp(2.5rem, 8vw, 5.4rem)", lineHeight: 0.98 },
  subtitle: { margin: 0, maxWidth: 900, color: "rgba(255,255,255,.82)", lineHeight: 1.65, fontSize: "clamp(1rem, 2.2vw, 1.18rem)" },
  deliveryNote: { marginTop: 18, borderRadius: 14, padding: "12px 14px", background: "rgba(255,255,255,.055)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.72)", lineHeight: 1.5 },
  adminLink: { display: "inline-flex", marginTop: 16, borderRadius: 999, padding: "11px 15px", textDecoration: "none", color: "#fff", fontWeight: 900, background: "linear-gradient(135deg,#da1724,#7e040c)", border: "1px solid rgba(255,82,82,.65)" },
  formCard: { marginTop: 18, borderRadius: 22, padding: "22px clamp(14px, 3vw, 28px)", border: "1px solid rgba(255,255,255,.12)", background: "rgba(5,5,8,.84)", boxShadow: "0 22px 60px rgba(0,0,0,.38)" },
  formHeadingRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 14 },
  formTitle: { margin: "6px 0 0", color: "#fff", fontSize: "clamp(1.8rem, 4vw, 3rem)" },
  accountPill: { borderRadius: 999, padding: "9px 12px", color: "#ffb4b9", background: "rgba(116,8,17,.35)", border: "1px solid rgba(255,82,92,.28)", fontWeight: 900, fontSize: 11, letterSpacing: ".08em" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14, marginTop: 20 },
  label: { color: "rgba(255,255,255,.78)", display: "grid", gap: 7, fontWeight: 800, lineHeight: 1.35 },
  optional: { color: "rgba(255,255,255,.45)", fontWeight: 600 },
  input: { width: "100%", boxSizing: "border-box", borderRadius: 12, border: "1px solid rgba(255,255,255,.16)", background: "rgba(0,0,0,.56)", color: "#fff", padding: "12px 13px", font: "inherit", outline: "none" },
  textarea: { resize: "vertical", lineHeight: 1.55 },
  honeypot: { position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" },
  success: { marginTop: 16, borderRadius: 14, padding: 13, color: "#a8f3bf", background: "rgba(17,106,49,.25)", border: "1px solid rgba(61,203,106,.28)", lineHeight: 1.5 },
  error: { marginTop: 16, borderRadius: 14, padding: 13, color: "#ffb7b7", background: "rgba(128,13,22,.26)", border: "1px solid rgba(255,76,88,.3)", lineHeight: 1.5 },
  actionRow: { marginTop: 18, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 },
  responseCopy: { color: "rgba(255,255,255,.52)", lineHeight: 1.45, maxWidth: 650, fontSize: 13 },
  submitButton: { borderRadius: 999, border: "1px solid rgba(255,82,82,.72)", background: "linear-gradient(135deg,#e21827,#82040d)", color: "#fff", padding: "12px 20px", fontWeight: 900, cursor: "pointer", fontSize: 15 },
};
